import {
  BulkDeleteCrmObjectsRequestInterface,
  CreateCrmObjectRequestInterface,
  CreateCrmObjectResponseInterface,
  CRMActivityApiService,
  DeleteCrmObjectRequestInterface,
  GetMultiCrmObjectRequestInterface,
  GetMultiCrmObjectResponseInterface,
  ListCrmObjectsRequestInterface,
  ListCrmObjectsResponseInterface,
  ListFieldOptionsRequestInterface,
  ListFiltersRequestInterface,
  ListFiltersResponseInterface,
  UpdateCrmObjectRequestInterface,
  ValidateCrmObjectRequestInterface,
  ValidateCrmObjectResponseInterface,
} from '@vendasta/crm';
import { AbstractCrmObjectService } from './_abstract.service';
import { Observable, of } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AtlasLanguageService } from '@galaxy/atlas/core';
import { getMultiActivity$, listActivities$ } from '@vendasta/protos/crm/v1/crmactivity-service';
import {
  toLegacyGetMultiActivitiesResponse,
  toLegacyListActivitiesResponse,
  toProtoGetMultiRequest,
  toProtoListActivitiesRequest,
} from '@galaxy/crm/services/adapter-service';

@Injectable({ providedIn: 'root' })
export class ActivityObjectService implements AbstractCrmObjectService {
  private readonly languageService = inject(AtlasLanguageService);
  private readonly activityApiService = inject(CRMActivityApiService);
  protected locale = toSignal(this.languageService.language$);

  createCrmObject(request: CreateCrmObjectRequestInterface): Observable<CreateCrmObjectResponseInterface> {
    return this.activityApiService.createActivity(request);
  }

  deleteCrmObject(request: DeleteCrmObjectRequestInterface): Observable<HttpResponse<null>> {
    return this.activityApiService.deleteActivity(request);
  }

  getMultiObject(request: GetMultiCrmObjectRequestInterface): Observable<GetMultiCrmObjectResponseInterface> {
    return getMultiActivity$(toProtoGetMultiRequest(request)).pipe(map(toLegacyGetMultiActivitiesResponse));
  }

  listFieldOptions(request: ListFieldOptionsRequestInterface): Observable<string[]> {
    return this.activityApiService.listActivityFieldOptions(request).pipe(map((response) => response?.options || []));
  }

  listObjects(request: ListCrmObjectsRequestInterface): Observable<ListCrmObjectsResponseInterface> {
    if (!request.namespace) {
      return of({
        crmObjects: [],
        pagingMetadata: {},
      } as ListCrmObjectsResponseInterface);
    }
    return listActivities$(toProtoListActivitiesRequest(request)).pipe(map(toLegacyListActivitiesResponse));
  }

  listObjectsFilters(request: ListFiltersRequestInterface): Observable<ListFiltersResponseInterface> {
    return this.activityApiService.listActivityFilters({ ...request, locale: this.locale() });
  }

  updateCrmObject(request: UpdateCrmObjectRequestInterface): Observable<HttpResponse<null>> {
    return this.activityApiService.updateActivity({
      namespace: request.namespace,
      activity: request.crmObject,
      fieldMask: request.fieldMask,
    });
  }

  validateCrmObject(request: ValidateCrmObjectRequestInterface): Observable<ValidateCrmObjectResponseInterface> {
    return this.activityApiService.validateActivity(request);
  }

  bulkDelete(request: BulkDeleteCrmObjectsRequestInterface): Observable<HttpResponse<null>> {
    return this.activityApiService.bulkDeleteActivities(request);
  }
}
